import { DESPESA_SALVAMENTO_SLUG } from '@/features/quotes/components/rd-equipamentos/steps/sections/coverage-rows.util';
import { useEffect, useMemo, useRef } from 'react';
import { formOptions, useStore } from '@tanstack/react-form';
import {
  Alert,
  Button,
  Field,
  RadioCard,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { lookupCep } from '@/features/quotes/utils/rd-cep-lookup.util';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { useRdAggregateProducts } from '@/features/quotes/hooks/rd-equipamentos/use-rd-aggregate-products.hook';
import { useRdIndemnityPeriods } from '@/features/quotes/hooks/rd-equipamentos/use-rd-indemnity-periods.hook';
import { useRdCoverages } from '@/features/quotes/hooks/rd-equipamentos/use-rd-coverages.hook';
import { useRdFrotaPlanos } from '@/features/quotes/hooks/rd-equipamentos/use-rd-frota-planos.hook';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useSetRdFleet } from '@/features/quotes/hooks/rd-equipamentos/use-set-rd-fleet.hook';
import type { StepFrotaFormValues } from '@/features/quotes/schemas/rd-equipamentos/step-frota.schema';
import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import {
  brlInputToCents,
  centsToBRL,
} from '@/features/quotes/utils/rd-units.util';
import { resolveLimit } from '@/features/quotes/utils/rd-equipamentos/rd-limit-rule.util';
import { formatCurrencyInputValue } from '@/shared/utils/currencyFormatter';
import { pickError } from './utils/step-helpers.util';
import { StepActions } from './sections/step-actions.component';

/**
 * `formatCurrencyInputValue` trunca em R$ 9.999.999,99 por padrão
 * (`CURRENCY_INPUT_MAX_DIGITS = 9`) — Valor em Risco/LMI de frota
 * rotineiramente excedem esse teto, então os campos monetários deste step
 * passam um `maxDigits` maior explicitamente.
 */
const FLEET_CURRENCY_MAX_DIGITS = 12;

const formOpts = formOptions({
  defaultValues: {
    riskValue: '',
    totalLmi: '',
    plano: '',
    fleetCoverages: {} as Record<string, string>,
    /** Prazo de indenização do agregado: um só para a proposta inteira. */
    indemnityMonths: 0,
    riskCep: '',
    riskAddress: '',
    riskNumber: '',
    riskComplement: '',
    riskDistrict: '',
    riskCity: '',
    riskState: '',
  } as StepFrotaFormValues,
});

/**
 * Step 2 (agregado) — Frota / LMI Único (RD Equipamentos, ramo 0171):
 * substitui Equipamentos + Coberturas para `productType` `frota`/`lmi_unico`
 * (ver `adaptStepsForFlow` em `new-rd-equipamentos-form.component.tsx`).
 * Coleta Valor em Risco, LMI total e o plano — o prêmio é calculado de forma
 * agregada (Valor em Risco × taxa), não por item; o quadro de coberturas
 * abaixo é apenas informativo para a apólice.
 */
export function StepFrota({
  onNext,
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const form = useQuoteForm({ ...formOpts });
  const { setFieldValue } = form;

  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();
  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );
  const { data: aggregateProducts, isLoading: isAggregateProductsLoading } =
    useRdAggregateProducts();
  const { data: coverages, isLoading: isCoveragesLoading } = useRdCoverages();
  const { data: frotaPlanos, isLoading: isFrotaPlanosLoading } =
    useRdFrotaPlanos();

  const isLmiUnico = proposal?.productType === 'lmi_unico';

  const { mutateAsync: fetchRiskZipcode, isPending: isFetchingRiskCep } =
    useFetchZipcode('quotation');
  const riskNumberContainerRef = useRef<HTMLDivElement>(null);
  const aggregateKey: 'frota' | 'lmi_unico' = isLmiUnico
    ? 'lmi_unico'
    : 'frota';

  const coverageSlugs = useMemo(
    () => aggregateProducts?.[aggregateKey]?.coverageSlugs ?? [],
    [aggregateProducts, aggregateKey]
  );

  const coverageNameBySlug = useMemo(
    () => new Map((coverages ?? []).map(c => [c.slug, c.name])),
    [coverages]
  );

  const coverageDefBySlug = useMemo(
    () => new Map((coverages ?? []).map(c => [c.slug, c])),
    [coverages]
  );

  const planoOptions = useMemo(
    () =>
      (frotaPlanos ?? [])
        .filter(plano => plano.product === aggregateKey)
        .map(plano => ({
          value: plano.slug,
          label: `${plano.name} — ${plano.description}`,
        })),
    [frotaPlanos, aggregateKey]
  );

  const { handleSubmit, isPending, submitAttempted, persistThenBack } =
    useSetRdFleet({
      proposalId: resolvedProposalId,
      form,
      onNext,
      onBack,
    });

  const hasPrefilledRef = useRef(false);
  useEffect(() => {
    if (!proposal || !aggregateProducts || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;

    setFieldValue(
      'riskValue',
      proposal.riskValue ? centsToBRL(proposal.riskValue) : ''
    );
    setFieldValue(
      'totalLmi',
      proposal.totalLmi ? centsToBRL(proposal.totalLmi) : ''
    );
    setFieldValue('plano', proposal.frotaPlano ?? '');
    setFieldValue('indemnityMonths', proposal.fleetIndemnityMonths ?? 0);
    setFieldValue('riskCep', proposal.riskCep ?? '');
    setFieldValue('riskAddress', proposal.riskAddress ?? '');
    setFieldValue('riskNumber', proposal.riskNumber ?? '');
    setFieldValue('riskComplement', proposal.riskComplement ?? '');
    setFieldValue('riskDistrict', proposal.riskDistrict ?? '');
    setFieldValue('riskCity', proposal.riskCity ?? '');
    setFieldValue('riskState', proposal.riskState ?? '');

    const initialCoverages: Record<string, string> = {};
    coverageSlugs.forEach(slug => {
      const existing = proposal.fleetCoverages?.[slug];
      initialCoverages[slug] = existing ? centsToBRL(existing) : '';
    });
    setFieldValue('fleetCoverages', initialCoverages);
  }, [proposal, aggregateProducts, coverageSlugs, setFieldValue]);

  const fleetCoverages = useStore(
    form.store,
    state => state.values.fleetCoverages
  );

  const indemnityMonths = useStore(
    form.store,

    state => state.values.indemnityMonths
  );

  // Cadastro: a lista e a ordem dos botões vêm do motor.

  const { data: indemnityPeriods } = useRdIndemnityPeriods();
  const riskValueDisplay = useStore(
    form.store,
    state => state.values.riskValue
  );
  const totalLmiDisplay = useStore(form.store, state => state.values.totalLmi);

  /**
   * G-2a: no LMI Único, LMI Total não pode exceder o Valor em Risco (razão
   * LMI/VR ≤ 1,0) — o motor já rejeita isso (`"LMI total acima do Valor em
   * Risco."`, ver `RdQuoteError`/`quote-breakdown.component.tsx`), mas até
   * aqui o Portal só descobria isso depois do PUT /fleet. Pré-valida no
   * client (comparação em centavos, via `brlInputToCents`) e bloqueia
   * "Próximo" antes de mandar o request. Frota não tem essa regra crua — o
   * motor clampa a razão LMI/VR ao maior bucket da tabela de taxas em vez de
   * rejeitar —, então o gate não se aplica a `frota`.
   */
  const lmiExceedsRiskValue =
    isLmiUnico &&
    brlInputToCents(totalLmiDisplay) > brlInputToCents(riskValueDisplay);

  /**
   * Pré-empta o 422 de `set_fleet` (`errors.risk_cep`): LMI Único é
   * estacionário e exige local do risco agregado.
   */
  const missingRiskCep = isLmiUnico && !form.state.values.riskCep?.trim();

  /**
   * Teto de cada cobertura do quadro agregado, resolvido contra a Básica
   * digitada aqui. Mesma regra do caminho individual: o que o `limitRule` do
   * cadastro impõe, nunca acima da Básica (invariante do motor, fora do
   * cadastro de propósito). O `set_fleet` passou a validar isso no servidor
   * — antes gravava o blob cru —, e este cálculo só antecipa a recusa.
   */
  const coverageLimits = useMemo(() => {
    const basicaCents = brlInputToCents(fleetCoverages?.basica ?? '');
    // No agregado o análogo do "valor do equipamento" (regra `<=value`) é o
    // Valor em Risco: o risco é um só.
    const ctx = {
      basicaCents,
      equipValueCents: brlInputToCents(riskValueDisplay),
    };
    const limits = new Map<string, number | null>();
    coverageSlugs.forEach(slug => {
      const def = coverageDefBySlug.get(slug);
      const fromRule = resolveLimit(def?.limitRule ?? null, ctx);
      if (slug === 'basica') {
        limits.set(slug, fromRule);
        return;
      }
      limits.set(
        slug,
        fromRule === null ? basicaCents : Math.min(fromRule, basicaCents)
      );
    });
    return limits;
  }, [coverageSlugs, coverageDefBySlug, fleetCoverages, riskValueDisplay]);

  /** Slugs cujo LMI passou do teto — bloqueiam o "Próximo". */
  const overLimitSlugs = coverageSlugs.filter(slug => {
    const max = coverageLimits.get(slug);
    if (max === null || max === undefined) return false;
    return brlInputToCents(fleetCoverages?.[slug] ?? '') > max;
  });

  function handleCoverageChange(slug: string, value: string) {
    // Cobertura automática (Despesa de Salvamento) é derivada pelo motor e
    // exibida readonly — digitar nela não deveria nem chegar aqui.
    if (coverageDefBySlug.get(slug)?.automatic) return;
    const current = form.state.values.fleetCoverages;
    setFieldValue('fleetCoverages', { ...current, [slug]: value });
  }

  const blockInteraction =
    isPending ||
    isProposalLoading ||
    isAggregateProductsLoading ||
    isCoveragesLoading ||
    isFrotaPlanosLoading;
  const disabled = isViewMode || blockInteraction;

  return (
    <div className='relative flex flex-col gap-8'>
      {isProposalLoading ||
      isAggregateProductsLoading ||
      isCoveragesLoading ||
      isFrotaPlanosLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando dados da proposta'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando dados da proposta...
          </Text>
        </div>
      ) : null}

      <Alert.Root status='info'>
        <Alert.Icon />
        <Alert.Content>
          <Alert.Description>
            O prêmio é agregado: Valor em Risco × taxa (da razão LMI/Valor em
            Risco e do plano) ÷ (1 − comissão) × desconto/agravo. Não é
            precificado por item.
            {isLmiUnico
              ? ' Estacionário exige o endereço do risco (informado abaixo, em "Local de risco").'
              : ''}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>

      <div className='flex flex-col gap-6'>
        <Text className='text-base text-alba-black-500'>
          Importância Segurada
        </Text>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
          <form.AppField
            name='riskValue'
            children={field => {
              const errorMessage = pickError(field.state.meta, submitAttempted);
              return (
                <Field.Root invalid={!!errorMessage}>
                  <Field.Label>
                    Valor em Risco (R$){' '}
                    <span className='text-alba-error-red-500'>*</span>
                  </Field.Label>
                  <Field.Input
                    type='text'
                    inputMode='decimal'
                    value={field.state.value}
                    onChange={e =>
                      field.handleChange(
                        formatCurrencyInputValue(
                          e.target.value,
                          FLEET_CURRENCY_MAX_DIGITS
                        )
                      )
                    }
                    placeholder='R$ 0,00'
                    disabled={disabled}
                  />
                  {errorMessage ? (
                    <Field.ErrorText>{errorMessage}</Field.ErrorText>
                  ) : (
                    <Field.HelperText>
                      {isLmiUnico
                        ? 'Valor total em risco'
                        : 'Valor total em risco da frota'}
                    </Field.HelperText>
                  )}
                </Field.Root>
              );
            }}
          />
          <form.AppField
            name='totalLmi'
            children={field => {
              const errorMessage = lmiExceedsRiskValue
                ? 'LMI total acima do Valor em Risco.'
                : pickError(field.state.meta, submitAttempted);
              return (
                <Field.Root invalid={!!errorMessage}>
                  <Field.Label>
                    LMI Total (R$){' '}
                    <span className='text-alba-error-red-500'>*</span>
                  </Field.Label>
                  <Field.Input
                    type='text'
                    inputMode='decimal'
                    value={field.state.value}
                    onChange={e =>
                      field.handleChange(
                        formatCurrencyInputValue(
                          e.target.value,
                          FLEET_CURRENCY_MAX_DIGITS
                        )
                      )
                    }
                    placeholder='R$ 0,00'
                    disabled={disabled}
                  />
                  {errorMessage ? (
                    <Field.ErrorText>{errorMessage}</Field.ErrorText>
                  ) : (
                    <Field.HelperText>
                      Limite máximo de indenização — define a razão LMI/VR
                    </Field.HelperText>
                  )}
                </Field.Root>
              );
            }}
          />
          <form.AppField
            name='plano'
            children={field => (
              <div className='flex flex-col gap-1'>
                <field.SelectField
                  label='Plano'
                  required
                  options={planoOptions}
                  disabled={disabled}
                />
                <Text className='text-xs text-alba-gray-500'>
                  Pacote de coberturas; define o multiplicador de taxa
                </Text>
              </div>
            )}
          />
        </div>
      </div>

      {/* Local de risco do agregado. LMI Único é estacionário
          (AGGREGATE_PRODUCT_MODALITY) e exige local; como o fluxo não tem
          itens, o endereço é o do risco agregado e vive na proposta.
          Frota (móvel) não exige. */}
      {isLmiUnico ? (
        <div className='flex flex-col gap-6'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Local de risco
          </Text>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
            <form.AppField
              name='riskCep'
              listeners={{
                onBlur: ({ value }) =>
                  lookupCep({
                    zipcode: value,
                    fetchFn: fetchRiskZipcode,
                    policyId: resolvedProposalId || undefined,
                    fields: {
                      address: 'riskAddress',
                      district: 'riskDistrict',
                      city: 'riskCity',
                      state: 'riskState',
                    },
                    setFieldValue,
                    onFilled: () =>
                      riskNumberContainerRef.current
                        ?.querySelector('input')
                        ?.focus(),
                  }),
              }}
              children={field => (
                <field.TextField
                  label='CEP'
                  mask='cep'
                  required
                  placeholder='00000-000'
                  disabled={disabled || isFetchingRiskCep}
                />
              )}
            />
            <div ref={riskNumberContainerRef}>
              <form.AppField
                name='riskNumber'
                children={field => (
                  <field.TextField label='Número' disabled={disabled} />
                )}
              />
            </div>
            <form.AppField
              name='riskComplement'
              children={field => (
                <field.TextField label='Complemento' disabled={disabled} />
              )}
            />
          </div>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <form.AppField
              name='riskAddress'
              children={field => (
                <field.TextField label='Endereço' disabled={disabled} />
              )}
            />
            <form.AppField
              name='riskDistrict'
              children={field => (
                <field.TextField label='Bairro' disabled={disabled} />
              )}
            />
          </div>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <form.AppField
              name='riskCity'
              children={field => (
                <field.TextField label='Cidade' disabled={disabled} />
              )}
            />
            <form.AppField
              name='riskState'
              children={field => (
                <field.TextField label='UF' maxLength={2} disabled={disabled} />
              )}
            />
          </div>
        </div>
      ) : null}

      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-1'>
          <Text className='text-base text-alba-black-500'>
            Detalhamento de coberturas (LMI por cobertura)
          </Text>
          <Text className='text-xs text-alba-gray-500'>
            Quadro informativo para a apólice; o prêmio vem do Valor em Risco,
            LMI e plano acima.
          </Text>
        </div>

        <div className='flex flex-col divide-y divide-alba-gray-300 rounded-xl border border-alba-gray-300 bg-white'>
          {coverageSlugs.map(slug => {
            const name = coverageNameBySlug.get(slug) ?? slug;
            const isDespesaSalvamento = slug === DESPESA_SALVAMENTO_SLUG;
            // Automática = derivada pelo motor (10% da Básica, de
            // `limit_rule`): exibe o valor calculado e não aceita digitação.
            const derived = Boolean(coverageDefBySlug.get(slug)?.automatic);
            const maxCents = coverageLimits.get(slug) ?? null;
            const overLimit = overLimitSlugs.includes(slug);
            const displayValue =
              derived && maxCents !== null
                ? centsToBRL(maxCents)
                : (fleetCoverages[slug] ?? '');
            return (
              <div
                key={slug}
                /* Empilha abaixo de `sm` pelo mesmo motivo da matriz de
                   coberturas por item: com a coluna fixa de 220px, a 375px o
                   nome da cobertura era espremido a quase zero (`min-w-0
                   flex-1`) e saía por baixo do campo — "RC Danos Materiais"
                   aparecia cortado. */
                className='flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-6'
              >
                <div className='flex min-w-0 flex-1 flex-col gap-2'>
                  <div className='flex min-w-0 items-center gap-2'>
                    <Text className='text-sm font-bold text-alba-black-500'>
                      {name}
                    </Text>
                    {isDespesaSalvamento && (
                      <Text className='text-xs text-alba-gray-500'>
                        (10% da Básica — fixo)
                      </Text>
                    )}
                  </div>
                  {/* Um prazo para a proposta inteira: o agregado não tem
                      itens, e a cobertura que o pede aparece uma vez aqui.
                      Só depois de contratada — no agregado, "contratada" é
                      LMI maior que zero, o mesmo critério do documento. */}
                  {coverageDefBySlug.get(slug)?.usesIndemnityPeriod &&
                  brlInputToCents(fleetCoverages[slug] || '') > 0 ? (
                    <div className='flex flex-col gap-1'>
                      <Text className='text-xs font-bold text-alba-black-500'>
                        Período indenitário
                        <span className='text-alba-error-red-500'>*</span>
                      </Text>
                      <RadioCard.Root
                        value={indemnityMonths ? String(indemnityMonths) : ''}
                        onValueChange={details =>
                          details.value &&
                          setFieldValue(
                            'indemnityMonths',
                            Number(details.value)
                          )
                        }
                      >
                        <div className='flex flex-wrap gap-2'>
                          {(indemnityPeriods ?? []).map(period => (
                            <RadioCard.Item
                              key={period.months}
                              value={String(period.months)}
                              disabled={disabled}
                            >
                              <RadioCard.ItemControl />
                              <RadioCard.ItemText>
                                {period.name}
                              </RadioCard.ItemText>
                              <RadioCard.ItemHiddenInput />
                            </RadioCard.Item>
                          ))}
                        </div>
                      </RadioCard.Root>
                    </div>
                  ) : null}
                </div>
                <div className='w-full sm:w-[220px] sm:shrink-0'>
                  <Field.Root invalid={overLimit}>
                    {/* O cabeçalho da coluna não existe neste quadro; no
                        empilhado o campo ficaria sem rótulo visível. */}
                    <Field.Label className='text-xs font-bold text-alba-black-500 sm:hidden'>
                      LMI
                    </Field.Label>
                    <Field.Input
                      type='text'
                      inputMode='decimal'
                      aria-label={`I.S. ${name}`}
                      value={displayValue}
                      onChange={e =>
                        handleCoverageChange(
                          slug,
                          formatCurrencyInputValue(
                            e.target.value,
                            FLEET_CURRENCY_MAX_DIGITS
                          )
                        )
                      }
                      placeholder='R$ 0,00'
                      disabled={disabled}
                      readOnly={derived}
                    />
                    {overLimit ? (
                      <Field.ErrorText>
                        I.S. acima do limite para esta cobertura.
                      </Field.ErrorText>
                    ) : !derived && maxCents !== null ? (
                      <Field.HelperText>
                        máximo {centsToBRL(maxCents)}
                      </Field.HelperText>
                    ) : null}
                  </Field.Root>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={persistThenBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2'
          onClick={handleSubmit}
          disabled={
            isViewMode ||
            blockInteraction ||
            lmiExceedsRiskValue ||
            missingRiskCep ||
            overLimitSlugs.length > 0
          }
        >
          {isPending ? (
            <>
              Processando...
              <Spinner size='sm' colorScheme='gray' />
            </>
          ) : (
            <>
              Próximo
              <ArrowRight className='h-6 w-6' strokeWidth={2} />
            </>
          )}
        </Button>
      </StepActions>
    </div>
  );
}
